<template>
  <div>
    <!-- Print Button (Hidden when printing) -->
    <div class="text-center mb-4 no-print">
      <button @click="printReceipt" class="btn btn-primary">
        <i class="fas fa-print"></i> Print Receipt
      </button>
      <button @click="downloadPDF" class="btn btn-success ms-2">
        <i class="fas fa-download"></i> Download PDF
      </button>
    </div>

    <!-- Form 51 Receipt Template -->
    <div id="form51-receipt" ref="receiptContent" class="receipt-container">
      <!-- BIR Header -->
      <div class="text-center bir-header mb-3">
        <div class="fw-bold" style="font-size: 14pt;">
          REPUBLIC OF THE PHILIPPINES<br>
          BUREAU OF INTERNAL REVENUE
        </div>
        <div class="fst-italic" style="font-size: 12pt;">
          (To be accomplished in triplicate)
        </div>
        <div class="fw-bold" style="font-size: 16pt; margin-top: 5px;">
          OFFICIAL RECEIPT<br>
          Form 51
        </div>
      </div>

      <!-- Company Information -->
      <div class="company-info mb-3">
        <div class="row">
          <div class="col-8">
            <div class="border-bottom" style="min-height: 30px;">
              <strong>VENDOR:</strong> {{ company.name }}
            </div>
            <div class="border-bottom" style="min-height: 30px;">
              <strong>ADDRESS:</strong> {{ company.address }}
            </div>
            <div class="border-bottom" style="min-height: 30px;">
              <strong>TIN:</strong> {{ company.tin }}
            </div>
          </div>
          <div class="col-4">
            <div class="border border-dark p-2 text-center">
              <div style="font-size: 10pt;">Serial No.</div>
              <div class="fw-bold" style="font-size: 14pt;">{{ receipt.serialNo }}</div>
            </div>
            <div class="border border-dark p-2 text-center mt-2">
              <div style="font-size: 10pt;">Date Issued</div>
              <div class="fw-bold" style="font-size: 12pt;">{{ formatDate(receipt.date) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Customer Information -->
      <div class="customer-info mb-3">
        <div class="border-bottom" style="min-height: 30px;">
          <strong>RECEIVED FROM:</strong> {{ customer.name }}
        </div>
        <div class="border-bottom" style="min-height: 30px;">
          <strong>ADDRESS:</strong> {{ customer.address }}
        </div>
        <div class="border-bottom" style="min-height: 30px;">
          <strong>TIN:</strong> {{ customer.tin }}
        </div>
        <div class="border-bottom" style="min-height: 30px;">
          <strong>BUSINESS STYLE:</strong> {{ customer.businessStyle }}
        </div>
      </div>

      <!-- Amount Section -->
      <div class="amount-section mb-3">
        <div class="border border-dark p-2">
          <div class="text-center fw-bold" style="font-size: 14pt;">
            THE SUM OF PESOS
          </div>
          <div class="text-center border border-dark bg-light py-2 my-2">
            <span style="font-size: 16pt; letter-spacing: 2px;">{{ amountInWords }}</span>
          </div>
          <div class="text-center fw-bold" style="font-size: 14pt;">
            ₱ {{ formatCurrency(receipt.amount) }}
          </div>
        </div>
      </div>

      <!-- Payment Details -->
      <div class="payment-details mb-3">
        <div class="border-bottom" style="min-height: 30px;">
          <strong>NATURE OF COLLECTION:</strong> {{ receipt.natureOfCollection }}
        </div>
        <div class="border-bottom" style="min-height: 30px;">
          <strong>MODE OF PAYMENT:</strong> {{ receipt.paymentMode }}
        </div>
        <div class="border-bottom" style="min-height: 30px;">
          <strong>REFERENCE (O.R./C.K. NO. & DATE):</strong> {{ receipt.reference }}
        </div>
      </div>

      <!-- Signatures -->
      <div class="signatures mt-4">
        <div class="row">
          <div class="col-6 text-center">
            <div class="border-top border-dark pt-2 mx-4">
              <div>Received by:</div>
              <div style="min-height: 50px; margin-top: 20px;"></div>
              <div class="fw-bold">{{ receipt.receivedBy }}</div>
              <div>Signature over Printed Name</div>
              <div>Position/Designation</div>
            </div>
          </div>
          <div class="col-6 text-center">
            <div class="border-top border-dark pt-2 mx-4">
              <div>Approved by:</div>
              <div style="min-height: 50px; margin-top: 20px;"></div>
              <div class="fw-bold">{{ receipt.approvedBy }}</div>
              <div>Signature over Printed Name</div>
              <div>Position/Designation</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="footer mt-4 pt-3 border-top">
        <div class="row">
          <div class="col-4 text-center">
            <div class="border p-1" style="font-size: 10pt;">
              <div>BIR PERMIT TO USE</div>
              <div>No. _______________</div>
              <div>Date: ______________</div>
            </div>
          </div>
          <div class="col-4 text-center">
            <div class="border p-1" style="font-size: 10pt;">
              <div>ACCREDITATION NO.</div>
              <div>_______________</div>
            </div>
          </div>
          <div class="col-4 text-center">
            <div class="border p-1" style="font-size: 10pt;">
              <div>VALID UNTIL</div>
              <div>_______________</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Form51Receipt',
  props: {
    // Receipt data passed from parent component
    receiptData: {
      type: Object,
      default: () => ({
        serialNo: '000001',
        date: new Date(),
        amount: 1500.00,
        natureOfCollection: 'Service Fee',
        paymentMode: 'Cash',
        reference: 'CK-2024-001 / ' + new Date().toLocaleDateString(),
        receivedBy: 'JUAN DELA CRUZ',
        approvedBy: 'MARIA SANTOS'
      })
    },
    companyData: {
      type: Object,
      default: () => ({
        name: 'SAMPLE COMPANY CORP.',
        address: '123 Main Street, Manila, Philippines',
        tin: '123-456-789-000'
      })
    },
    customerData: {
      type: Object,
      default: () => ({
        name: 'JOHN DOE',
        address: '456 Elm Street, Quezon City',
        tin: '987-654-321-000',
        businessStyle: 'Retail'
      })
    }
  },
  data() {
    return {
      receipt: { ...this.receiptData },
      company: { ...this.companyData },
      customer: { ...this.customerData }
    }
  },
  computed: {
    amountInWords() {
      return this.numberToWords(this.receipt.amount)
    }
  },
  methods: {
    printReceipt() {
      window.print()
    },
    
    downloadPDF() {
      // You can implement PDF generation using libraries like jsPDF
      // For now, this triggers print which can be saved as PDF
      window.print()
    },
    
    formatDate(date) {
      const d = new Date(date)
      return d.toLocaleDateString('en-PH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    
    formatCurrency(amount) {
      return amount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')
    },
    
    numberToWords(num) {
      // Basic number to words converter (expand as needed)
      const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine']
      //const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']
      const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen']
      
      // For demonstration - implement full logic as needed
      if (num === 0) return 'Zero'
      if (num <= 9) return ones[Math.floor(num)]
      if (num <= 19) return teens[Math.floor(num) - 10]
      
      // Return placeholder - implement full conversion based on your needs
      return 'One Thousand Five Hundred Only'
    }
  },
  mounted() {
    // Initialize with current date if not provided
    if (!this.receipt.date) {
      this.receipt.date = new Date()
    }
  }
}
</script>

<style scoped>
/* Official Form 51 Styling */
@media print {
  @page {
    size: A4;
    margin: 0.5cm;
  }
  body {
    margin: 0;
    font-family: 'Times New Roman', serif;
    background: white !important;
    color: black !important;
  }
  .no-print {
    display: none !important;
  }
  .receipt-container {
    border: 2px solid #000 !important;
    padding: 15px !important;
    background: white !important;
  }
}

.receipt-container {
  border: 2px solid #000;
  padding: 15px;
  font-family: 'Times New Roman', serif;
  font-size: 12pt;
  line-height: 1.2;
  background: white;
  max-width: 21cm;
  margin: 0 auto;
}

.bir-header {
  border-bottom: 2px solid #000;
  padding-bottom: 10px;
  margin-bottom: 15px;
}

.border-bottom {
  border-bottom: 1px solid #000 !important;
  padding: 4px 0;
}

/* Ensure proper spacing for print */
@media screen {
  .receipt-container {
    background: #f8f9fa;
    box-shadow: 0 0 10px rgba(0,0,0,0.1);
  }
}

/* Bootstrap-like spacing utilities */
.mb-3 { margin-bottom: 1rem !important; }
.mt-4 { margin-top: 1.5rem !important; }
.pt-3 { padding-top: 1rem !important; }
.ms-2 { margin-left: 0.5rem !important; }
</style>